// LandingHeader — T-066. Sleek top navigation bar with above-the-fold
// language switcher (DE / HU / EN), PWA install trigger, and login CTA.
// Sticky on desktop and mobile viewports without competing with hero typography.

import type { JSX } from "react";
import { useI18n } from "../../i18n";
import { persistLocale } from "../../i18n/detectLocale";
import type { Locale } from "../../types/i18n";
import { InstallButton } from "../pwa/InstallButton";
import { ThemeToggle } from "./ThemeToggle";

const LANG_ORDER: Locale[] = ["de", "hu", "en"];
const LANG_LABEL: Record<Locale, string> = { de: "DE", hu: "HU", en: "EN" };

export function LandingHeader(): JSX.Element {
  const { t, locale, setLocale } = useI18n();

  const onPick = (next: Locale) => {
    persistLocale(next);
    setLocale(next);
  };

  return (
    <header
      role="banner"
      className="sticky top-0 z-40 w-full border-b border-[var(--color-line)]/80 bg-[var(--color-void)]/90 backdrop-blur-md transition-all"
    >
      <div className="mx-auto flex h-16 max-w-[1280px] items-center justify-between px-4 sm:px-8">
        <a
          href="/"
          className="font-display text-2xl uppercase tracking-[0.02em] text-[var(--color-lime)] hover:opacity-90"
        >
          {t("brand.name")}
        </a>

        <div className="flex items-center gap-3 sm:gap-6">
          {/* Language Switcher */}
          <nav aria-label="language selector" className="flex items-center">
            <div className="flex items-center border border-[var(--color-line-strong)] bg-[var(--color-slab)] p-0.5" style={{ borderRadius: "var(--radius-sharp)" }}>
              {LANG_ORDER.map((l) => {
                const selected = locale === l;
                return (
                  <button
                    key={l}
                    type="button"
                    onClick={() => onPick(l)}
                    aria-pressed={selected}
                    data-testid={"header-lang-" + l}
                    className={
                      "px-2.5 py-1 font-subhead text-xs uppercase tracking-[0.06em] transition-colors " +
                      (selected
                        ? "bg-[var(--color-lime)] font-bold text-[var(--color-void)]"
                        : "text-[var(--color-ink)] hover:text-[var(--color-ink-hi)]")
                    }
                    style={{ borderRadius: "var(--radius-sharp)" }}
                  >
                    {LANG_LABEL[l]}
                  </button>
                );
              })}
            </div>
          </nav>

          <ThemeToggle />

          <InstallButton variant="header" />

          <a
            href="/login"
            className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink)] hover:text-[var(--color-lime)] transition-colors"
          >
            {t("landing.header.login")}
          </a>
        </div>
      </div>
    </header>
  );
}
